import type { GridCell, GridPosition, MascotCareAction, MascotId, MascotSaveData, MascotSavedState, MascotState } from "../types";
import { getMascotDefinition } from "../entities/MascotDefinitions";

export interface MascotTask {
  position: GridPosition;
  action: MascotCareAction;
  source: "autonomous" | "player";
}

export interface MascotCareResult { success: boolean; gpAwarded: number }
export interface MascotSystemOptions {
  width: number;
  height: number;
  getCell: (position: GridPosition) => GridCell | undefined;
  discoverTasks: () => readonly Omit<MascotTask, "source">[];
  executeCare: (action: MascotCareAction, position: GridPosition, mascotId: MascotId) => MascotCareResult;
  addGP?: (amount: number) => void;
  random?: () => number;
  restAreas?: readonly GridPosition[];
  onGrowth?: (event: { mascotId: MascotId; levelUp: boolean; affinityEmotes: readonly string[] }) => void;
}

const keyOf = (p: GridPosition): string => `${p.x},${p.y}`;
const distance = (a: GridPosition, b: GridPosition): number => Math.abs(a.x - b.x) + Math.abs(a.y - b.y);
const clamp = (value: number, low: number, high: number): number => Math.max(low, Math.min(high, value));
const experienceForLevel = (level: number): number => 40 + level * level * 20;
const actionDuration = (specialty: boolean): number => specialty ? 1.25 : 1.8;
const actionCost = (level: number, specialty: boolean): number => Math.max(5, 14 * (specialty ? .72 : 1) * (1 - Math.min(.3, (level - 1) * .025)));

export class MascotSystem {
  private readonly states = new Map<MascotId, MascotState>();
  private readonly locks = new Map<string, MascotId>();
  private readonly random: () => number;
  private unlocked: MascotId[];
  private active: MascotId[];
  private maxSlots: number;
  private patrolElapsed = 0;

  constructor(private readonly options: MascotSystemOptions, save?: Partial<MascotSaveData>) {
    this.random = options.random ?? Math.random;
    this.unlocked = [...(save?.unlockedMascots ?? ["gnome_default" as MascotId])];
    this.maxSlots = clamp(Math.floor(save?.maxMascotSlots ?? 1), 1, 5);
    this.active = [...(save?.activeMascotIds ?? ["gnome_default" as MascotId])].filter((id, index, all) => all.indexOf(id) === index && this.unlocked.includes(id)).slice(0, this.maxSlots);
    for (const id of this.unlocked) this.states.set(id, this.createState(id, save?.mascotStatus?.[id]));
  }

  get activeMascots(): readonly MascotState[] { return this.active.map((id) => this.states.get(id)).filter((state): state is MascotState => state !== undefined); }
  get unlockedMascotIds(): readonly MascotId[] { return this.unlocked; }
  get activeMascotIds(): readonly MascotId[] { return this.active; }
  get maximumSlots(): number { return this.maxSlots; }

  getWage(id: MascotId): number {
    const def = getMascotDefinition(id);
    if (!def) return 0;
    const state = this.states.get(id);
    const affinity = state?.affinity ?? 0;
    return Math.round(def.wagePerDayGP * (1 - (affinity / 100) * 0.3));
  }

  getTotalDailyWage(): number {
    return this.active.reduce((sum, id) => sum + this.getWage(id), 0);
  }

  deactivateMascot(id: MascotId): boolean {
    const index = this.active.indexOf(id);
    if (index === -1) return false;
    this.active.splice(index, 1);
    const state = this.states.get(id);
    if (state) {
      this.release(state);
      state.assignedAction = null;
      state.currentAction = "resting";
      this.showEmote(state, "zzz", 3);
    }
    return true;
  }

  update(deltaSeconds: number): void {
    const dt = clamp(deltaSeconds, 0, .25);
    this.patrolElapsed += dt;
    for (const state of this.activeMascots) this.updateMascot(state, dt);
    if (this.patrolElapsed >= 1) { this.patrolElapsed = 0; this.assignAutonomousTasks(); }
  }

  requestCare(action: MascotCareAction, position: GridPosition): MascotId | null {
    if (!this.inBounds(position) || this.locks.has(keyOf(position))) return null;
    const candidates = this.activeMascots.filter((state) => state.assignedAction === null && state.currentAction !== "resting");
    candidates.sort((a, b) => {
      const ad = getMascotDefinition(a.id)?.specialtyActions.includes(action) ? 0 : 1;
      const bd = getMascotDefinition(b.id)?.specialtyActions.includes(action) ? 0 : 1;
      return ad - bd || distance(a.position, position) - distance(b.position, position);
    });
    const chosen = candidates[0];
    if (!chosen) return null;
    this.assign(chosen, { action, position: { ...position }, source: "player" });
    return chosen.id;
  }

  pet(id: MascotId): boolean {
    const state = this.states.get(id);
    if (!state || !this.unlocked.includes(id)) return false;
    state.affinity = clamp(state.affinity + 2, 0, 100);
    state.currentAction = "petting";
    state.actionElapsed = 0;
    this.showEmote(state, "heart", 1.8);
    return true;
  }

  giveTreat(id: MascotId, affinityBoost = 8, experienceBoost = 12): boolean {
    const state = this.states.get(id);
    if (!state || !this.unlocked.includes(id) || affinityBoost < 0 || experienceBoost < 0) return false;
    const before = state.affinity;
    state.affinity = clamp(state.affinity + affinityBoost, 0, 100);
    state.currentAction = "eatingTreat";
    state.actionElapsed = 0;
    this.addExperience(state, experienceBoost, before);
    this.showEmote(state, "heart", 2.4);
    return true;
  }

  unlock(id: MascotId): boolean {
    if (this.unlocked.includes(id) || !getMascotDefinition(id)) return false;
    this.unlocked.push(id);
    this.states.set(id, this.createState(id));
    return true;
  }

  setActive(id: MascotId, active: boolean): boolean {
    if (!this.unlocked.includes(id)) return false;
    if (active) {
      if (this.active.includes(id)) return true;
      if (this.active.length >= this.maxSlots) return false;
      this.active.push(id);
      return true;
    }
    this.release(this.states.get(id));
    this.active = this.active.filter((candidate) => candidate !== id);
    return true;
  }

  expandSlots(): boolean {
    if (this.maxSlots >= 5) return false;
    this.maxSlots += 1;
    return true;
  }

  serialize(): MascotSaveData {
    const mascotStatus: Record<string, MascotSavedState> = {};
    for (const [id, state] of this.states) mascotStatus[id] = { level: state.level, experience: state.experience, affinity: state.affinity, position: { ...state.position }, currentAction: state.currentAction, currentStamina: state.currentStamina };
    return { unlockedMascots: [...this.unlocked], activeMascotIds: [...this.active], maxMascotSlots: this.maxSlots, mascotStatus, saveVersion: "3.1" };
  }

  private updateMascot(state: MascotState, dt: number): void {
    state.actionElapsed += dt;
    if (state.emoteRemaining > 0) { state.emoteRemaining -= dt; if (state.emoteRemaining <= 0) state.emote = null; }
    if (state.currentAction === "petting" || state.currentAction === "eatingTreat") {
      if (state.actionElapsed >= 1.2) { state.currentAction = "idle"; state.actionElapsed = 0; }
      return;
    }
    if (state.target && distance(state.position, state.target) > .08) { this.moveToward(state, state.target, dt); return; }
    if (state.target) {
      state.position = { ...state.target };
      state.target = null;
      state.actionElapsed = 0;
      if (!state.assignedAction) {
        state.currentAction = state.currentStamina < (getMascotDefinition(state.id)?.stamina ?? 100) * .2 ? "resting" : "idle";
      }
    }
    if (state.assignedAction) { this.performCare(state); return; }
    if (state.currentAction === "resting") {
      const definition = getMascotDefinition(state.id);
      state.currentStamina = Math.min(definition?.stamina ?? 100, state.currentStamina + dt * 20);
      this.showEmote(state, "zzz", .5);
      if (definition && state.currentStamina >= definition.stamina * .9) { state.currentAction = "idle"; state.actionElapsed = 0; }
      return;
    }
    const definition = getMascotDefinition(state.id);
    if (definition && state.currentStamina < definition.stamina * .2) { this.sendToRest(state); return; }
  }

  private assignAutonomousTasks(): void {
    const tasks = this.options.discoverTasks();
    for (const state of this.activeMascots) {
      if (state.assignedAction || state.target || state.currentAction === "resting") continue;
      const definition = getMascotDefinition(state.id);
      if (!definition) continue;
      if (state.currentStamina < definition.stamina * .2) { this.sendToRest(state); continue; }
      const available = tasks.filter((task) => !this.locks.has(keyOf(task.position)));
      available.sort((a, b) => (definition.specialtyActions.includes(a.action) ? 0 : 1) - (definition.specialtyActions.includes(b.action) ? 0 : 1) || distance(state.position, a.position) - distance(state.position, b.position));
      const task = available[0];
      if (task) this.assign(state, { ...task, source: "autonomous" }); else if (this.random() < .45) this.patrol(state);
    }
  }

  private assign(state: MascotState, task: MascotTask): void {
    this.release(state);
    state.target = { ...task.position };
    state.assignedAction = task.action;
    state.currentAction = "walking";
    state.actionElapsed = 0;
    this.locks.set(keyOf(task.position), state.id);
  }

  private performCare(state: MascotState): void {
    const action = state.assignedAction;
    const definition = getMascotDefinition(state.id);
    if (!action || !definition) { this.release(state); return; }
    state.currentAction = action;
    const specialty = definition.specialtyActions.includes(action);
    if (state.actionElapsed < actionDuration(specialty)) return;
    const successChance = Math.min(.98, .72 + (state.level - 1) * .025 + (specialty ? .15 : 0));
    const result = this.random() <= successChance ? this.options.executeCare(action, state.position, state.id) : { success: false, gpAwarded: 0 };
    state.currentStamina = Math.max(0, state.currentStamina - actionCost(state.level, specialty));
    if (result.success) {
      const bonus = specialty ? 2 : 0;
      if (this.options.addGP && bonus > 0) this.options.addGP(bonus);
      const before = state.affinity;
      state.affinity = clamp(state.affinity + (specialty ? 2 : 1), 0, 100);
      this.addExperience(state, 10 + Math.min(10, result.gpAwarded) + bonus, before);
      this.showEmote(state, "heart", 1.2);
    }
    this.release(state);
  }

  private addExperience(state: MascotState, amount: number, affinityBefore: number): void {
    state.experience += Math.max(0, Math.floor(amount));
    let levelUp = false;
    while (state.level < 50 && state.experience >= experienceForLevel(state.level)) { state.experience -= experienceForLevel(state.level); state.level += 1; levelUp = true; }
    const definition = getMascotDefinition(state.id);
    const affinityEmotes = definition?.emotes.filter((emote) => emote.requiredAffinity > affinityBefore && emote.requiredAffinity <= state.affinity).map((emote) => emote.id) ?? [];
    if (levelUp || affinityEmotes.length) this.options.onGrowth?.({ mascotId: state.id, levelUp, affinityEmotes });
  }

  private moveToward(state: MascotState, target: GridPosition, dt: number): void {
    const definition = getMascotDefinition(state.id);
    const dx = target.x - state.position.x, dy = target.y - state.position.y, length = Math.hypot(dx, dy);
    const speed = (definition?.speed ?? 1) * (1 + Math.min(.35, (state.level - 1) * .02));
    const step = Math.min(length, speed * dt);
    state.direction = dx < 0 ? -1 : dx > 0 ? 1 : state.direction;
    state.position = { x: state.position.x + dx / length * step, y: state.position.y + dy / length * step };
    state.currentAction = "walking";
  }

  private patrol(state: MascotState): void {
    for (let attempts = 0; attempts < 8; attempts += 1) {
      const position = { x: Math.floor(this.random() * this.options.width), y: Math.floor(this.random() * this.options.height) };
      const cell = this.options.getCell(position);
      if (cell && cell.terrain !== "water") { state.target = position; state.currentAction = "walking"; state.actionElapsed = 0; return; }
    }
  }

  private sendToRest(state: MascotState): void {
    const areas = this.options.restAreas?.filter((position) => this.inBounds(position)) ?? [];
    const fallback = [{ x: 0, y: 0 }, { x: this.options.width - 1, y: 0 }, { x: 0, y: this.options.height - 1 }];
    const target = [...areas, ...fallback].sort((a, b) => distance(state.position, a) - distance(state.position, b))[0];
    this.release(state);
    state.target = target ? { ...target } : null;
    state.currentAction = state.target ? "walking" : "resting";
    state.actionElapsed = 0;
  }

  private release(state: MascotState | undefined): void {
    if (!state) return;
    for (const [key, owner] of this.locks) if (owner === state.id) this.locks.delete(key);
    state.target = null;
    state.assignedAction = null;
    state.currentAction = state.currentAction === "resting" ? "resting" : "idle";
    state.actionElapsed = 0;
  }

  private showEmote(state: MascotState, emote: string, seconds: number): void { state.emote = emote; state.emoteRemaining = Math.max(state.emoteRemaining, seconds); }
  private inBounds(position: GridPosition): boolean { return position.x >= 0 && position.y >= 0 && position.x < this.options.width && position.y < this.options.height; }
  private createState(id: MascotId, saved?: MascotSavedState): MascotState {
    const definition = getMascotDefinition(id);
    const position = saved?.position && this.inBounds(saved.position) ? saved.position : { x: 0, y: 0 };
    return { id, level: clamp(Math.floor(saved?.level ?? 1), 1, 50), experience: Math.max(0, Math.floor(saved?.experience ?? 0)), affinity: clamp(saved?.affinity ?? 0, 0, 100), position: { ...position }, currentAction: saved?.currentAction ?? "idle", currentStamina: clamp(saved?.currentStamina ?? definition?.stamina ?? 100, 0, definition?.stamina ?? 100), target: null, assignedAction: null, actionElapsed: 0, direction: 1, emote: null, emoteRemaining: 0 };
  }
}
